import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useNotifications } from '../contexts/NotificationContext';
import tokenStorage from '../utils/tokenStorage';

/**
 * WebSocket Hook
 * Manages real-time Socket.io connection and event handling.
 *
 * SHARED SOCKET: A single socket is shared across all pages for the same sessionId.
 * This prevents disconnection when participants navigate between pages (Active → Notes → Workbooks).
 * Previously, each page created its own socket — on unmount it emitted 'leave-session' and disconnected,
 * causing a gap where the participant missed events and the server thought they left.
 *
 * Safari-resilient: refreshes token on reconnect, handles pagehide, validates auth on visibility change.
 */

// Determine socket URL at runtime based on current environment
const getSocketUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    // ORIGIN ONLY — socket.io reads a URL path as a NAMESPACE. Passing
    // "http://localhost:5050/api" verbatim connects to namespace "/api", which the
    // server never registers, so every socket dies with "Invalid namespace" in any
    // environment that sets this var (i.e. all local dev). Production never hit it
    // because the var is unset there and the same-origin branch below runs instead.
    try {
      return new URL(process.env.REACT_APP_API_URL).origin;
    } catch {
      return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
    }
  }
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1';
  if (!isLocalhost) {
    return window.location.origin;
  }
  return 'http://localhost:5000';
};

const SOCKET_URL = getSocketUrl();

/**
 * Read participant data from dual storage (with auto-restore from sessionStorage)
 */
const getParticipantFromStorage = (sessionId) => {
  return tokenStorage.getParticipantData(sessionId) || {};
};

/**
 * Handshake credentials, read fresh from storage each time they are needed:
 *   token            — a signed-in user's JWT (trainer / client / admin)
 *   participantToken — a participant's signed identity for THIS session; the server
 *                      verifies it and puts the socket in that participant's private room
 */
const buildSocketAuth = (sessionId) => {
  const auth = {};
  const token = tokenStorage.getToken();
  const participantToken = tokenStorage.getParticipantToken(sessionId);
  if (token) auth.token = token;
  if (participantToken) auth.participantToken = participantToken;
  return auth;
};

// ─── Shared socket cache ───────────────────────────────────────────────
// One socket per sessionId, shared across all pages/components.
// Ref-counted so it only disconnects when the LAST consumer unmounts.
const socketCache = new Map(); // sessionId -> { socket, refCount, listeners }

const getOrCreateSocket = (sessionId, auth) => {
  if (socketCache.has(sessionId)) {
    const entry = socketCache.get(sessionId);
    entry.refCount++;
    // Cancel any pending destroy — a new page picked up the socket
    if (entry.destroyTimer) {
      clearTimeout(entry.destroyTimer);
      entry.destroyTimer = null;
    }
    console.log(`[WebSocket] Reusing socket for session ${sessionId} (refCount: ${entry.refCount})`);
    return entry;
  }

  const socket = io(SOCKET_URL, {
    auth,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    pingTimeout: 60000,
    pingInterval: 25000,
  });

  const entry = { socket, refCount: 1, coreHandlersAttached: false, listeners: { visibility: null, focus: null, pagehide: null } };
  socketCache.set(sessionId, entry);
  console.log(`[WebSocket] Created new socket for session ${sessionId}`);
  return entry;
};

const releaseSocket = (sessionId) => {
  const entry = socketCache.get(sessionId);
  if (!entry) return;

  entry.refCount--;
  console.log(`[WebSocket] Released socket for session ${sessionId} (refCount: ${entry.refCount})`);

  if (entry.refCount <= 0) {
    // Fix #5: do NOT destroy immediately. React unmounts the old page BEFORE
    // mounting the new one, so refCount briefly hits 0 during page navigation
    // (Active → Notes → Workbooks). 15s delay accommodates Safari BFCache and
    // slow devices. On timeout, disconnect the socket but do NOT emit
    // 'leave-session' — the server's 10-minute grace period handles cleanup.
    // Emitting 'leave-session' would bypass the grace period and mark the
    // participant left instantly, kicking them out of the session.
    if (entry.destroyTimer) clearTimeout(entry.destroyTimer);
    entry.destroyTimer = setTimeout(() => {
      // Re-check — a new page may have mounted and incremented refCount
      if (entry.refCount <= 0) {
        entry.socket.disconnect();

        if (entry.listeners.visibility) document.removeEventListener('visibilitychange', entry.listeners.visibility);
        if (entry.listeners.focus) window.removeEventListener('focus', entry.listeners.focus);
        if (entry.listeners.pagehide) window.removeEventListener('pagehide', entry.listeners.pagehide);

        socketCache.delete(sessionId);
        console.log(`[WebSocket] Socket destroyed for session ${sessionId} (no consumers after 15s delay — server grace timer will handle leave)`);
      } else {
        console.log(`[WebSocket] Socket kept alive for session ${sessionId} (refCount recovered to ${entry.refCount})`);
      }
    }, 15000);
  }
};
// ────────────────────────────────────────────────────────────────────────

const useWebSocket = (sessionId, options = {}) => {
  const { notifyActivityLaunch } = useNotifications();
  const [isConnected, setIsConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const socketRef = useRef(null);
  const eventHandlersRef = useRef({});

  const on = useCallback((event, handler) => {
    if (!eventHandlersRef.current[event]) {
      eventHandlersRef.current[event] = [];
    }
    eventHandlersRef.current[event].push(handler);
    if (socketRef.current) {
      socketRef.current.on(event, handler);
    }
  }, []);

  const off = useCallback((event, handler) => {
    if (eventHandlersRef.current[event]) {
      eventHandlersRef.current[event] = eventHandlersRef.current[event].filter(
        (h) => h !== handler
      );
    }
    if (socketRef.current) {
      socketRef.current.off(event, handler);
    }
  }, []);

  // Fix #6: queue important events when offline, replay on reconnect. The
  // submit-response server handler is already idempotent (deduplicates by
  // participant), so at-least-once delivery is safe.
  // `sim:lock-in` is queued because losing a team's committed decision to a network
  // blip would stall the whole room. It is only safe to queue because the server
  // handler is idempotent by construction (update-if-present, else guarded push), so
  // the at-least-once redelivery below cannot create a duplicate commit.
  const QUEUED_EVENTS = ['submit-response', 'update-results', 'sim:lock-in'];
  const MAX_QUEUE_SIZE = 50;
  const QUEUE_STORAGE_KEY = `pending_emits_${sessionId}`;

  const emit = useCallback((event, data) => {
    if (socketRef.current && socketRef.current.connected) {
      console.log('[WebSocket] Emit:', event, data);
      socketRef.current.emit(event, data);
    } else if (QUEUED_EVENTS.includes(event)) {
      // Queue critical events so they are not silently lost during a network
      // blip. Drained on reconnect via drainQueue() below.
      try {
        const raw = sessionStorage.getItem(QUEUE_STORAGE_KEY);
        const queue = raw ? JSON.parse(raw) : [];
        if (queue.length >= MAX_QUEUE_SIZE) queue.shift(); // drop oldest
        queue.push({ event, data, timestamp: Date.now() });
        sessionStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
        console.warn('[WebSocket] Offline — queued event for retry:', event, `(queue size: ${queue.length})`);
      } catch (err) {
        console.error('[WebSocket] Failed to queue emit:', err);
      }
    } else {
      console.warn('[WebSocket] Cannot emit, not connected');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [QUEUE_STORAGE_KEY]);

  // Replay queued events after reconnection. Called from `connect` and
  // `reconnect` handlers below. Drops entries older than 10 min (stale).
  const drainQueue = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(QUEUE_STORAGE_KEY);
      if (!raw) return;
      const queue = JSON.parse(raw);
      if (!queue.length) return;

      const now = Date.now();
      const TEN_MIN = 10 * 60 * 1000;
      const fresh = queue.filter((e) => now - (e.timestamp || 0) < TEN_MIN);
      const stale = queue.length - fresh.length;

      if (stale > 0) console.warn(`[WebSocket] Dropped ${stale} stale queued events`);
      console.log(`[WebSocket] Draining ${fresh.length} queued events on reconnect`);

      fresh.forEach(({ event, data }) => {
        if (socketRef.current?.connected) {
          socketRef.current.emit(event, data);
        }
      });
      sessionStorage.removeItem(QUEUE_STORAGE_KEY);
    } catch (err) {
      console.error('[WebSocket] Failed to drain queue:', err);
    }
  }, [QUEUE_STORAGE_KEY]);

  /**
   * Initialize or reuse Socket.io connection
   */
  useEffect(() => {
    if (!sessionId || options.disabled) return;

    const entry = getOrCreateSocket(sessionId, buildSocketAuth(sessionId));
    const socket = entry.socket;
    socketRef.current = socket;

    // Helper: join session room with participant data
    const joinSessionRoom = () => {
      const participantData = getParticipantFromStorage(sessionId);
      const participantId = participantData.participantId || participantData.id;
      socket.emit('join-session', sessionId, {
        participantId,
        email: participantData.email,
        name: participantData.name,
        organization: participantData.organization,
      });
      console.log('[WebSocket] Joined session:', sessionId, 'as:', participantData.name || participantData.email || 'anonymous');

      // Send client-side device extras the server can't get from User-Agent
      // (screen size, network type, language, timezone). Used by the live
      // session monitor.
      if (participantId) {
        try {
          const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
          socket.emit('participant:device-info', {
            sessionId,
            participantId,
            screen: { width: window.screen?.width, height: window.screen?.height },
            network: conn?.effectiveType || 'unknown',
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
        } catch (err) {
          console.warn('[WebSocket] device-info emit failed:', err?.message);
        }
      }
    };

    // ─── Core socket event handlers (only attached once per socket) ───
    // Use a flag to prevent duplicate handler registration (React Strict Mode
    // can remount with refCount=1 after the destroy timer is cancelled)
    const needsCoreHandlers = !entry.coreHandlersAttached;

    if (needsCoreHandlers) {
      entry.coreHandlersAttached = true;
      socket.on('connect', () => {
        console.log('[WebSocket] Connected to server');
        setIsConnected(true);
        setReconnecting(false);
        joinSessionRoom();
        drainQueue(); // Fix #6: replay any events that were queued while offline
      });

      socket.on('disconnect', (reason) => {
        console.log('[WebSocket] Disconnected:', reason);
        setIsConnected(false);
      });

      socket.on('connect_error', (error) => {
        console.error('[WebSocket] Connection error:', error.message);
        setIsConnected(false);
      });

      // Reconnection events live on the Manager (socket.io) in socket.io-client v4, not
      // on the Socket. The previous socket.on('reconnecting' | 'reconnect') listeners
      // never fired, so `reconnecting` stayed false and session-rehydrate was never sent.
      socket.io.on('reconnect_attempt', () => {
        console.log('[WebSocket] Reconnecting...');
        setReconnecting(true);
        // Refresh credentials before each attempt (Safari ITP fix)
        socket.auth = buildSocketAuth(sessionId);
      });

      socket.io.on('reconnect', () => {
        console.log('[WebSocket] Reconnected');
        setReconnecting(false);
        // The socket's own 'connect' handler (above) already re-joins the session room
        // and drains the offline queue on every (re)connect, so neither is repeated here.
        // Fix #7: tell listeners to re-hydrate session state from REST
        window.dispatchEvent(new CustomEvent('session-rehydrate', { detail: { sessionId } }));
      });

      socket.io.on('reconnect_failed', () => {
        setReconnecting(false);
      });

      // Server tells us the supplied JWT was expired/invalid. For authenticated
      // users (trainer/client) this means they would silently operate as
      // anonymous and lose privileges — warn them and send them to /login.
      // For anonymous participants (no user record) we ignore: they don't need
      // a JWT at all (they're identified by participantId headers/cookies).
      socket.on('auth:expired', ({ reason } = {}) => {
        console.warn('[WebSocket] auth:expired from server:', reason);
        const storedUser = tokenStorage.getUser?.();
        const hasUser = !!(storedUser && (storedUser.role === 'trainer' || storedUser.role === 'client' || storedUser.role === 'super-admin'));
        if (!hasUser) return; // anonymous participant — nothing to refresh

        // Token truly expired — clear and redirect. Don't clear participant data.
        try {
          tokenStorage.removeToken?.();
          tokenStorage.removeUser?.();
        } catch (e) {}

        // Avoid redirect loops / noisy UX if already on a login-ish path
        const path = window.location.pathname;
        if (!path.startsWith('/login') && !path.startsWith('/join')) {
          // Small delay so any in-flight session work can save
          setTimeout(() => { window.location.href = '/login?reason=session-expired'; }, 500);
        }
      });

      // Global browser event listeners (one set per socket, not per page)
      entry.listeners.visibility = () => {
        // Tell the server when the user backgrounds/foregrounds the tab so
        // the live monitor can classify disconnects (locked phone vs closed
        // tab vs lost network) instead of just seeing 'ping timeout'.
        // Pass the participant's identity so the server can attribute the
        // event even when this is a freshly reconnected socket that hasn't
        // re-fired join-session yet (otherwise the live feed shows "Someone").
        const lifecyclePayload = (() => {
          const p = getParticipantFromStorage(sessionId);
          return {
            participantId: p?.participantId || p?.id,
            participantName: p?.name,
          };
        })();

        if (document.visibilityState === 'hidden' && socket?.connected) {
          try { socket.emit('participant:backgrounded', lifecyclePayload); } catch (_) {}
        }

        if (document.visibilityState === 'visible' && socket) {
          try { if (socket.connected) socket.emit('participant:foregrounded', lifecyclePayload); } catch (_) {}

          socket.auth = buildSocketAuth(sessionId);
          if (!socket.connected) {
            console.log('[WebSocket] Tab foregrounded, socket disconnected — reconnecting');
            socket.connect();
          } else {
            // Fix #13: Safari sometimes reports connected=true after tab
            // resume while the underlying TCP is dead (zombie socket). Send
            // a heartbeat and force reconnect if no ack within 5 seconds.
            const participantData = getParticipantFromStorage(sessionId);
            let ackReceived = false;

            const ackHandler = () => {
              ackReceived = true;
              socket.off('participant:heartbeat:ack', ackHandler);
              console.log('[WebSocket] Heartbeat ack received — socket is alive');
            };
            socket.on('participant:heartbeat:ack', ackHandler);

            socket.emit('participant:heartbeat', {
              sessionId,
              participantId: participantData.participantId || participantData.id,
              timestamp: Date.now(),
            });

            setTimeout(() => {
              if (!ackReceived) {
                console.warn('[WebSocket] Heartbeat timeout — zombie socket, forcing reconnect');
                socket.off('participant:heartbeat:ack', ackHandler);
                socket.disconnect();
                setTimeout(() => socket.connect(), 500);
              }
            }, 5000);

            joinSessionRoom();
          }
        }
      };
      entry.listeners.focus = () => {
        if (socket && !socket.connected) {
          socket.auth = buildSocketAuth(sessionId);
          socket.connect();
        }
      };
      entry.listeners.pagehide = (e) => {
        if (!e.persisted && socket.connected) {
          console.log('[WebSocket] Page hiding (Safari)');
          // Tab is being destroyed — tell the server so it can classify the
          // imminent disconnect as 'closed-tab' rather than 'network-lost'.
          try { socket.emit('participant:closing'); } catch (_) {}
        }
      };

      document.addEventListener('visibilitychange', entry.listeners.visibility);
      window.addEventListener('focus', entry.listeners.focus);
      window.addEventListener('pagehide', entry.listeners.pagehide);
    } else {
      // Reusing existing socket — sync connection state
      setIsConnected(socket.connected);
      setReconnecting(false);

      // Re-join room in case we navigated away and back
      if (socket.connected) {
        joinSessionRoom();
      }
    }

    // Register this component's event handlers on the shared socket
    Object.entries(eventHandlersRef.current).forEach(([event, handlers]) => {
      handlers.forEach((handler) => {
        socket.on(event, handler);
      });
    });

    // Activity launch notification (per-component, safe to attach multiple times)
    const activityLaunchHandler = (data) => {
      if (notifyActivityLaunch) notifyActivityLaunch(data);
    };
    socket.on('activity:launched', activityLaunchHandler);

    // Cleanup: remove THIS component's handlers, release ref count
    return () => {
      // Remove only this component's handlers (not the core connection handlers)
      Object.entries(eventHandlersRef.current).forEach(([event, handlers]) => {
        handlers.forEach((handler) => {
          socket.off(event, handler);
        });
      });
      socket.off('activity:launched', activityLaunchHandler);

      releaseSocket(sessionId);
    };
  }, [sessionId, options.disabled, notifyActivityLaunch]);

  const disconnect = useCallback(() => {
    const entry = socketCache.get(sessionId);
    if (entry) {
      // Cancel any pending destroy timer
      if (entry.destroyTimer) clearTimeout(entry.destroyTimer);
      // Clean up global event listeners
      if (entry.listeners.visibility) document.removeEventListener('visibilitychange', entry.listeners.visibility);
      if (entry.listeners.focus) window.removeEventListener('focus', entry.listeners.focus);
      if (entry.listeners.pagehide) window.removeEventListener('pagehide', entry.listeners.pagehide);
    }
    if (socketRef.current) {
      socketRef.current.emit('leave-session', sessionId);
      socketRef.current.disconnect();
    }
    socketCache.delete(sessionId);
    setIsConnected(false);
    console.log('[WebSocket] Manually disconnected');
  }, [sessionId]);

  const joinRoom = useCallback((roomId) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('join-session', roomId);
    }
  }, []);

  const leaveRoom = useCallback((roomId) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('leave-session', roomId);
    }
  }, []);

  return useMemo(() => ({
    isConnected,
    reconnecting,
    on,
    off,
    emit,
    disconnect,
    joinRoom,
    leaveRoom,
    socket: socketRef.current,
  }), [isConnected, reconnecting, on, off, emit, disconnect, joinRoom, leaveRoom]);
};

export default useWebSocket;
